<template>
  <div class="locations">
    <page-header :title="title" :title-in-breadcrumbs="titleInBreadcrumbs" />
    <div class="public-container public-container-mobile">
      <locations-discount
        :is-one-location="!isRegionPage"
        :region="region"
        :tags="tags"
        :ref-calculator="refCalculator"
      />

      <locations-calculator-wrapper>
        <vz-calculator 
          ref="refCalculator" 
          promocode="VEZET15" 
        />
      </locations-calculator-wrapper>

      <template v-if="isRegionPage">
        <locations-tags
          :tags="tags"
          :location="from"
          class="mb-110"
        />
        <locations-offer class="mt-110-90-70-70" :region="region" />
      </template>

      <template v-else>
        <popular-directions
          id="locations-popular-directions"
          title-h3
          title="Стоимость грузовых перевозок" 
          class="mt-110" 
        />
      </template>
    </div>

    <locations-pickup-points v-if="region.id" :region="region" />

    <template v-if="!isRegionPage">
      <div class="public-container public-container-mobile">
        <locations-tags
          show-as-list
          :tags="tags"
          :location="from"
          :title="locationsTagsTitle"
          class="mb-110"
        />
        <div class="run-schedule-block mt-110">
          <run-schedule-directions
            title-h3
            hide-schedule-collapse 
            :title="runSheduleTitle" 
          />
        </div>
      </div>
    </template>

    <div class="public-container public-container-mobile">
      <template v-if="isRegionPage">
        <h3 class="vz-text-align-left mt-110-90-70-70">
          {{ regionContactsTerminalsTitle }}
        </h3>
        <locations-contacts-terminals is-from />
        <locations-tags
          show-as-list
          :tags="tags"
          :location="from"
          :title="locationsTagsTitle"
          class="mb-110"
        />
      </template>

      <template v-else>
        <locations-ordering />
      </template>
    </div>

    <template v-if="!isRegionPage">
      <div class="locations-contacts-terminals mt-110">
        <div class="public-container public-container-mobile">
          <h3> {{ contactsTerminalsTitle }}</h3>
          <contacts-terminals
            :location-guid="from?.guid" 
          >
            <contacts-departments />
          </contacts-terminals>
        </div>
      </div>
    </template>

    <div class="public-container public-container-mobile">
      <locations-cargo is-one-location class="my-90" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useCityStore } from '~/stores/city'
import { setYM } from '@/common/functions'
import { getCityByTranslit } from '~~/api/city'
import { useTerminalStore } from '~/stores/terminal';
import { Location, LocationRegion } from '~/types/types'
import { usePopularDirectionsStore } from '~/stores/popular-directions';
import PageHeader from '~~/components/public/page-header.vue'
import LocationsOrdering from '~~/components/public/locations/locations-ordering.vue'
import PopularDirections from '~~/components/public/popular-directions/popular-directions.vue'
import LocationsTags from '~/components/public/locations/locations-tags.vue'
import RunScheduleDirections from "~/components/public/run-schedule/run-schedule-directions.vue";
import ContactsTerminals from '~/components/public/contacts/contacts-terminals.vue';
import ContactsDepartments from '~/components/public/contacts/contacts-departments.vue';
import LocationsDiscount from '~/components/public/locations/locations-discount/locations-discount.vue';
import LocationsCargo from '~/components/public/locations/locations-cargo.vue'
import VzCalculator from '~/components/public/order/vz-calculator.vue';
import LocationsPickupPoints from '~/components/public/locations/locations-pickup-points.vue'
import LocationsCalculatorWrapper from '~/components/public/locations/locations-calculator-wrapper.vue'
import LocationsOffer from '~/components/public/locations/locations-offer/locations-offer.vue'
import LocationsContactsTerminals from '~/components/public/locations/locations-contacts-terminals.vue'

const { setTo, setFrom } = useCityStore()
const { from, to, rclCityList } = storeToRefs(useCityStore())
const { oneLocation, twoLocation } = storeToRefs(usePopularDirectionsStore())
const { terminalList } = storeToRefs(useTerminalStore())
const route = useRoute()

const region = ref({} as LocationRegion)
const refCalculator = ref()

async function getTags(locationFrom: string) {
  let newCityDispatch = {} as Location | undefined
  let newCityDestination = {} as Location | undefined

  const locationTo = locationFrom === 'moskva' ? 'sankt-peterburg' : 'moskva';

  const cityByTranslit = await getCityByTranslit({
    city: locationFrom,
    path: route.path,
    orderWay: `${locationFrom}_${locationTo}`,
    isOneLocation: true
  })

  if (cityByTranslit.error || !cityByTranslit.from) {
    showError({ statusCode: 404})
    return;
  }

  if ((route.params.locations === locationFrom) && cityByTranslit?.from?.regionL) {
    region.value = cityByTranslit.from.regionL
  }

  if (cityByTranslit?.from) {
    oneLocation.value = cityByTranslit?.from
  }
  if (cityByTranslit?.to) {
    twoLocation.value = cityByTranslit?.to
  }

  if (from.value && locationFrom !== from.value.translit) {
    newCityDispatch = rclCityList.value.find(c => [c.abbr, c.translit].includes(locationFrom))

    if (newCityDispatch) {
      setFrom(newCityDispatch)
    }
  }

  if (to.value && locationTo !== to.value.translit) {
    newCityDestination = rclCityList.value.find(c => [c.abbr, c.translit].includes(locationTo))
    if (newCityDestination) {
      setTo(newCityDestination)
    }
  }

  if (cityByTranslit?.from && !newCityDispatch) {
    setFrom(cityByTranslit.from)
  }
  if (cityByTranslit?.to && !newCityDestination) {
    setTo(cityByTranslit.to)
  }

  // seo - по Нижегородской области должны отрабатывать страницы только из Нижнего Новгорода или Дзержинска
  if (from.value && from.value.rcl_guid === '1d016bfc-0127-11e5-80c7-00155d903d03' &&
      from.value.guid !== '1d016bfd-0127-11e5-80c7-00155d903d03') {
    showError({ statusCode: 404})
  }

  return cityByTranslit.tags
}

const tags = ref(await getTags(route.params.locations as string))

// страница по области
const isRegionPage = computed(() => {
  return !!region.value?.id
})

const locationText = computed(() => {
  return isRegionPage.value 
    ? `${region.value.name} ${region.value?.type}` 
    : `${from.value?.type}. ${from.value.name}`
})

const title = computed(() => {
  return isRegionPage.value 
    ? `Грузоперевозки по ${region.value?.dative || locationText.value}` 
    : `Грузоперевозки в ${from.value?.prepositional_name || locationText.value}`
})

const titleInBreadcrumbs = computed(() => {
  return isRegionPage.value 
    ? title.value 
    : from?.value?.name
})

const runSheduleTitle = computed(() => {
  return `Расписание межтерминальных рейсов из ${from.value?.prepositional_name2 || locationText.value}`
})

const locationsTagsTitle = computed(() => {
  return `Терминалы в ${from.value?.prepositional_name || (from.value?.type + '. ' + from.value.name )} работают с нас. пунктами:`
})

const regionContactsTerminalsTitle = computed(() => {
  return `Терминалы в ${region.value?.prepositional || locationText.value}`
})

const contactsTerminalsTitle = computed(() => {
  return `Терминалы ТК "Возовоз" в ${from.value?.prepositional_name || locationText.value}`
})

watch(() => from.value, async () => {
  if (from.value.translit) {
    tags.value =  Number(from.value?.fake) === 1 ? [] : await getTags(from.value.translit)
  }
})

onMounted(() => {
  setYM(38058485, 'gorod-otpravleniya')
})
</script>